'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewRun } from '@/data/reviews/use-review-statuses';
import type { InstallationRepositories } from '@/types/github';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { REPOSITORY_DATA_SOURCE } from '@/data/installations/constants';
import { installationRepositoriesQueryKey } from '@/data/installations/queries';
import { onboardingStatusQueryKey } from '@/data/onboarding/use-onboarding-status';
import { reviewRunsQueryKey } from '@/data/reviews/use-review-statuses';
import { ReviewMode } from '@/generated/prisma/enums';
import { TRIGGER_SOURCE } from '@/lib/ai/code-review-service/trigger-review-source';
type TriggerUltrareviewResponse = {
  success: boolean;
  message: string;
  traceId?: string;
  /**
   * True when the route merged this request into a review that was triggered for
   * the same PR moments earlier (the trigger coalesce window). When set, no new
   * ultrareview was dispatched, so the success toast must not claim one started.
   */
  coalesced?: boolean;
};
type TriggerUltrareviewErrorResponse = {
  error?: string;
};
type TriggerUltrareviewMutationContext = {
  previousReviewRuns?: ReviewRun[];
  hadPreviousReviewRuns: boolean;
  queryKey?: ReturnType<typeof reviewRunsQueryKey>;
  optimisticRunId?: string;
};
type TriggerUltrareviewParams = {
  owner: string;
  repo: string;
  number: string;
  installationId: number;
  repositoryId?: number;
};

/**
 * Builds a single optimistic "general" run so the reviews UI renders immediately
 * while the ultrareview job is queued. Ultrareview surfaces as a deeper general
 * run (there is no dedicated ULTRAREVIEW run kind), so one general placeholder is
 * enough; it reconciles by traceId once the persisted run materializes.
 */
function buildOptimisticUltrareviewRun(seed: number = Date.now(), requestedAtIso: string = new Date().toISOString()): ReviewRun {
  const optimisticId = `optimistic-ultrareview-${seed}`;
  return {
    id: optimisticId,
    reviewRunId: optimisticId,
    traceId: '',
    status: 'running',
    runKind: 'general',
    violationCount: 0,
    createdAt: requestedAtIso,
    updatedAt: requestedAtIso,
    isPendingOptimistic: true,
    optimisticRequestedAt: requestedAtIso
  };
}
async function triggerUltrareview(params: TriggerUltrareviewParams) {
  const {
    owner,
    repo,
    number,
    installationId,
    repositoryId
  } = params;
  const response = await fetch(`/api/github/repos/${owner}/${repo}/pulls/${number}/trigger-review-new`, {
    method: 'POST',
    body: JSON.stringify({
      installationId,
      repositoryId,
      reviewScope: {
        kind: 'full'
      },
      triggerSource: TRIGGER_SOURCE.UI_MANUAL,
      mode: ReviewMode.ULTRAREVIEW
    })
  });
  if (!response.ok) {
    let errorResponse: TriggerUltrareviewErrorResponse | undefined;
    try {
      errorResponse = (await response.json()) as TriggerUltrareviewErrorResponse;
    } catch (error) {
      console.info('Failed to parse error response as json:', error);
    }

    // The route returns user-facing `error` strings for ultrareview-specific
    // rejections (not eligible, PR author not seated, quota reached), so surface
    // them verbatim.
    if (errorResponse?.error) {
      throw new Error(errorResponse.error);
    }
    throw new Error('Failed to trigger ultrareview');
  }
  return (await response.json()) as TriggerUltrareviewResponse;
}
export function useTriggerUltrareview() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: triggerUltrareview,
      onMutate: async variables => {
        const {
          owner,
          repo,
          number,
          installationId
        } = variables;
        if (!owner || !repo || !number) {
          console.error("[useTriggerUltrareview] Missing owner, repo, or number \u2013 aborting mutation");
          throw new Error("Missing required PR parameters");
        }
        const installationKey = installationRepositoriesQueryKey(installationId, REPOSITORY_DATA_SOURCE.DB);
        const installationData = queryClient.getQueryData(installationKey);
        const repositoryId = installationData?.repositories.find(r => r.name.toLowerCase() === repo.toLowerCase() && r.owner?.login.toLowerCase() === owner.toLowerCase())?.id;
        variables.repositoryId = repositoryId;
        const queryKey = reviewRunsQueryKey(owner, repo, number);
        await queryClient.cancelQueries({
          queryKey
        });
        const previousReviewRuns = queryClient.getQueryData(queryKey);
        const hadPreviousReviewRuns = previousReviewRuns !== undefined;
        const optimisticSeed = Date.now();
        const requestedAtIso = new Date(optimisticSeed).toISOString();
        const optimisticRun = buildOptimisticUltrareviewRun(optimisticSeed, requestedAtIso);
        queryClient.setQueryData(queryKey, t1 => {
          const oldData = t1 === undefined ? [] : t1;
          return [...(oldData || []), optimisticRun];
        });
        return {
          previousReviewRuns,
          hadPreviousReviewRuns,
          queryKey,
          optimisticRunId: optimisticRun.reviewRunId
        };
      },
      onSuccess: (data, _variables, context) => {
        toast.success(data?.coalesced ? "A review was just triggered for this PR \u2014 showing its progress." : "Ultrareview started. This may take a few minutes\u2026");
        if (!context?.queryKey || !context?.optimisticRunId) {
          return;
        }
        const optimisticRunId = context.optimisticRunId;
        const traceId = data?.traceId;
        if (!traceId) {
          queryClient.setQueryData(context.queryKey, t2 => {
            const runs = t2 === undefined ? [] : t2;
            return runs.filter(run => !(run.isPendingOptimistic && run.reviewRunId === optimisticRunId));
          });
          return;
        }
        const reconciledAtIso = new Date().toISOString();
        queryClient.setQueryData(context.queryKey, t3 => {
          const runs_0 = t3 === undefined ? [] : t3;
          return runs_0.map(run_0 => run_0.isPendingOptimistic && run_0.reviewRunId === optimisticRunId ? {
            ...run_0,
            traceId,
            updatedAt: reconciledAtIso
          } : run_0);
        });
      },
      onError: (error, _variables_0, context_0) => {
        console.error("Error triggering ultrareview:", error);
        if (error.message === "Subscription missing") {
          toast.error("Your subscription is currently inactive. Please reactivate your subscription to run ultrareviews.");
        } else {
          toast.error(error.message || "Failed to start ultrareview");
        }
        if (!context_0?.queryKey) {
          return;
        }
        if (context_0.hadPreviousReviewRuns) {
          queryClient.setQueryData(context_0.queryKey, context_0.previousReviewRuns);
          return;
        }
        queryClient.removeQueries({
          queryKey: context_0.queryKey,
          exact: true
        });
      },
      onSettled: (_data, _error, variables_0) => {
        if (variables_0?.installationId) {
          queryClient.invalidateQueries({
            queryKey: onboardingStatusQueryKey(variables_0.installationId)
          });
        }
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}